import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthApiService } from '@api-new/authservice';
import { NotificationApiService } from '@api-new/notificationservice';
import { AppState } from '@capacitor/app';
import { Device } from '@capacitor/device';
import { PushNotifications } from '@capacitor/push-notifications';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { paths } from '@platform/paths';
import { loginSuccess } from '@shared/store/auth/auth.action';
import { firstValueFrom } from 'rxjs';
import { AnalyticsService } from './analytics.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  private readonly analytics = inject(AnalyticsService);
  private readonly router = inject(Router);
  private readonly notificationApiService = inject(NotificationApiService);
  private readonly authApiService = inject(AuthApiService);
  private notificationInitialized = false;
  private readonly globalStore = inject(Store<AppState>);

  constructor() {
    this.listenTuPushNotifications();
  }

  initPushNotifications(userId: string): void {
    if (!userId || !environment.mobileApp?.isMobileApp || this.notificationInitialized) return;

    this.registerNotifications();
    this.addPushNotificationListeners();

    this.notificationInitialized = true;
  }

  registerNotifications = async (): Promise<void> => {
    let permStatus = await PushNotifications.checkPermissions();

    if (permStatus.receive === 'prompt') {
      permStatus = await PushNotifications.requestPermissions();
    }

    if (permStatus.receive !== 'granted') {
      this.analytics.setPushNotificationsEnabled(false);
      throw new Error('User denied permissions!');
    }
    this.analytics.setPushNotificationsEnabled(false);
    await PushNotifications.register();
  };

  addPushNotificationListeners = async (): Promise<void> => {
    await PushNotifications.addListener('registration', (token) => {
      this.registerPushDevice(token.value);
    });

    await PushNotifications.addListener('registrationError', (err) => {
      console.error('Registration error: ', err.error);
    });

    await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      this.analytics.notificationReceived();
    });
  };

  async listenTuPushNotifications(): Promise<void> {
    if (environment.mobileApp?.isMobileApp)
      await PushNotifications.addListener('pushNotificationActionPerformed', async (notification) => {
        this.reactOnNotificationOpen(notification);
      });
  }

  registerPushDevice = async (pushNotificationToken: string): Promise<void> => {
    const deviceId = await Device.getId();
    await firstValueFrom(
      this.notificationApiService.HTTP_CP_SetPushNotificationToken({ deviceId: deviceId.identifier, pushNotificationToken }),
    );
  };

  async reactOnNotificationOpen(notification): Promise<void> {
    this.analytics.notificationClicked();
    if (!notification.notification?.data) return;

    const email = notification.notification.data.clientUserEmail;
    const verificationCode = notification.notification.data.signInCode;
    const companyId = notification.notification.data.companyId;
    const redirectUrl = `/${paths.PLATFORM}/${paths.platform.PROPERTIES}`;

    const signInResponse = await firstValueFrom(
      this.authApiService.HTTP_CP_SignIn_WithCode({
        email: email,
        code: verificationCode,
        profileCompanyId: companyId,
        signInCompanyId: companyId,
      }),
    );
    this.globalStore.dispatch(
      loginSuccess({
        signInResponse,
        link: redirectUrl,
      }),
    );
  }
}
